/**
 * Haal documenten uit de dossier-bibliotheek binnen en zet ze om naar tijdelijke upload-URL’s
 * voor /analyze_documents (OpenAI ondersteunt hier image-URL’s met temp/-keys).
 */
import { api } from './api';
import { pdfToImageFiles } from './pdf-utils';
import type { ProjectLibraryDocument } from '../types/project';

export type AnalyzeDocumentRef = { type: 'image' | 'pdf'; url: string; label?: string };

function isPdfDoc(d: ProjectLibraryDocument): boolean {
  const ct = (d.content_type ?? '').toLowerCase();
  if (ct.includes('pdf')) return true;
  const lab = (d.label ?? '').toLowerCase();
  return lab.endsWith('.pdf');
}

function isImageDoc(d: ProjectLibraryDocument): boolean {
  const ct = (d.content_type ?? '').toLowerCase();
  return ct.startsWith('image/');
}

/**
 * Per bibliotheekdocument: fetch blob → upload tijdelijke afbeelding(en).
 * PDF’s worden client-side naar JPEG-pagina’s gerenderd (zelfde pad als nieuwe upload).
 */
export async function libraryDocumentsToAnalyzeRefs(
  docs: ProjectLibraryDocument[],
  tenantId: string
): Promise<AnalyzeDocumentRef[]> {
  const out: AnalyzeDocumentRef[] = [];
  const baseLabel = (d: ProjectLibraryDocument) => (d.label?.trim() ? d.label.trim() : 'Document');

  for (const d of docs) {
    const blob = await api.fetchDocumentBlob(d.url);
    const name = baseLabel(d);

    if (isPdfDoc(d)) {
      const file = new File([blob], `${name}.pdf`, { type: 'application/pdf' });
      const pages = await pdfToImageFiles(file);
      for (let j = 0; j < pages.length; j++) {
        const { url } = await api.uploadDocument(pages[j], tenantId);
        out.push({ type: 'image', url, label: `${name} p.${j + 1}` });
      }
      continue;
    }

    if (isImageDoc(d) || blob.type.startsWith('image/')) {
      const mime = blob.type || d.content_type || 'image/jpeg';
      const ext = mime.includes('png') ? 'png' : mime.includes('webp') ? 'webp' : mime.includes('gif') ? 'gif' : 'jpg';
      const file = new File([blob], `${name}.${ext}`, { type: mime });
      const { url } = await api.uploadDocument(file, tenantId);
      out.push({ type: 'image', url, label: name });
      continue;
    }

    throw new Error(
      `Bestand "${name}" in de bibliotheek heeft een onbekend type. Gebruik PNG, JPEG, WebP, GIF of PDF.`
    );
  }

  return out;
}
